import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCertificateNFTStore } from "../store/certificateNFTStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Certificate NFT Issuer (v1)
 * 
 * UI for issuing ESG certificate NFTs, managing revocation,
 * and tracking blockchain transactions.
 */
export default function CertificateNFT() {
  const { t } = useTranslation();
  const {
    companyID,
    esgScore,
    certificateLevel,
    issuanceTimestampISO,
    expiryTimestampISO,
    blockchainTxHash,
    verifierRegion,
    revocationRisk,
    setCompanyID,
    setEsgScore,
    setVerifierRegion,
    issueCertificate,
    revokeCertificate,
    exportCertificateEnvelope,
  } = useCertificateNFTStore();

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("CertificateNFTPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("companyID", companyID);
    useAIContext.getState().setInput("esgScore", esgScore);
    useAIContext.getState().setInput("certificateLevel", certificateLevel);
    useAIContext.getState().setInput("verifierRegion", verifierRegion);
    useAIContext.getState().setInput("revocationRisk", revocationRisk);
  }, [companyID, esgScore, certificateLevel, verifierRegion, revocationRisk]);

  const issuanceCostEUR = useCertificateNFTStore((s) => s.getIssuanceCostEUR());
  const validityDays = useCertificateNFTStore((s) => s.getValidityDays());
  const revocationProbability = useCertificateNFTStore((s) => s.getRevocationProbability());

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleIssue = async () => {
    if (!companyID || esgScore === 0) {
      setError(new Error(t("certificateNFT.invalidInput") || "Company ID and ESG score required"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await issueCertificate();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("certificateNFT.issueFailed") || "Failed to issue certificate");
      setError(error);
      console.error("Issue certificate failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!blockchainTxHash) {
      setError(new Error(t("certificateNFT.noTxHash") || "No transaction hash available"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await revokeCertificate();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("certificateNFT.revokeFailed") || "Failed to revoke certificate");
      setError(error);
      console.error("Revoke certificate failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!blockchainTxHash) {
      setError(new Error(t("certificateNFT.noTxHash") || "No transaction hash available"));
      return;
    }
    
    setError(null);
    try {
      exportCertificateEnvelope();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("certificateNFT.exportFailed") || "Failed to export certificate");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("certificateNFT.title") || "Certificate NFT Issuer"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="md" text={t("certificateNFT.processing") || "Processing..."} />}

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("certificateNFT.companyID") || "Company ID"}
          </label>
          <input
            type="text"
            value={companyID}
            onChange={(e) => setCompanyID(e.target.value)}
            placeholder="e.g., DE-COMPANY-001"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("certificateNFT.esgScore") || "ESG Score (0-100)"}
          </label>
          <div className="space-y-2">
            <input
              type="range"
              min="0"
              max="100"
              value={esgScore}
              onChange={(e) => setEsgScore(Number(e.target.value))}
              className="w-full"
            />
            <div className="text-center text-evat-gold font-orbitron">{esgScore}</div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("certificateNFT.verifierRegion") || "Verifier Region"}
          </label>
          <select
            value={verifierRegion}
            onChange={(e) => setVerifierRegion(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="EU">EU</option>
            <option value="EEA">EEA</option>
            <option value="National">National</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleIssue}
          disabled={!companyID || esgScore === 0 || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("certificateNFT.processing") || "Processing...") : (t("certificateNFT.issue") || "Issue Certificate NFT")}
        </button>
        <button
          onClick={handleRevoke}
          disabled={!blockchainTxHash || revocationRisk === 1.0 || loading}
          className="px-4 py-2 bg-red-500/20 border border-red-500 text-red-400 rounded hover:bg-red-500/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("certificateNFT.processing") || "Processing...") : (t("certificateNFT.revoke") || "Revoke Certificate NFT")}
        </button>
      </div>

      {/* Certificate Information Display */}
      {blockchainTxHash && (
        <div className="space-y-4 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.certificateLevel") || "Certificate Level"}
              </div>
              <div className="text-lg font-orbitron text-evat-gold uppercase">
                {certificateLevel}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.issuanceCostEUR") || "Issuance Cost"}
              </div>
              <div className="text-lg font-orbitron text-evat-gold">
                €{issuanceCostEUR.toFixed(2)}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.validityDays") || "Validity Days"}
              </div>
              <div className="text-lg font-orbitron text-evat-neon">
                {validityDays} days
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.revocationProbability") || "Revocation Probability"}
              </div>
              <div className={`text-lg font-orbitron ${
                revocationProbability < 0.1 ? "text-green-400" :
                revocationProbability < 0.3 ? "text-yellow-400" :
                "text-red-400"
              }`}>
                {(revocationProbability * 100).toFixed(1)}%
              </div>
            </div>
          </div>

          <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
            <div className="text-xs text-neutral-400 mb-2">
              {t("certificateNFT.blockchainTxHash") || "Blockchain Transaction Hash"}
            </div>
            <div className="font-mono text-xs text-evat-neon break-all">
              {blockchainTxHash}
            </div>
          </div>

          {issuanceTimestampISO && (
            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.issuanceTimestamp") || "Issuance Timestamp"}
              </div>
              <div className="text-sm text-neutral-300">{issuanceTimestampISO}</div>
            </div>
          )}

          {expiryTimestampISO && (
            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("certificateNFT.expiryTimestamp") || "Expiry Timestamp"}
              </div>
              <div className="text-sm text-neutral-300">{expiryTimestampISO}</div>
            </div>
          )}

          {revocationRisk === 1.0 && (
            <div className="border border-red-500/40 rounded p-3 bg-red-900/20 text-red-300">
              ⚠️ {t("certificateNFT.revoked") || "Certificate has been revoked"}
            </div>
          )}
        </div>
      )}

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={!blockchainTxHash || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("certificateNFT.export") || "Export Certificate JSON"}
        >
          {t("certificateNFT.export") || "Export Certificate JSON"}
        </button>
      </div>
    </div>
  );
}






